import React, { useState, useMemo } from 'react';
import ToolShell, { ACCENT, INK, fmt, roundTo, Field, input, card } from '../tools/ToolShell';

/*
 * PUBLIC, no-login lead-magnet tool: /tools/instagram-money-calculator
 * Targets the high-volume India query "instagram money calculator" / "how much
 * do reels pay in india". Unlike YouTube, Instagram has no per-view AdSense for
 * most Indian creators — earnings come from BRAND DEALS (sponsored Reels/posts/
 * stories), priced by followers x niche x engagement. This tool estimates that
 * rate. One-shot & stateless (no save/account) — funnels to CreatorKhata for the
 * pricing / invoicing / payment-tracking the app actually does.
 *
 * Built entirely from <ToolShell> — this file is ONLY the tool's config + math
 * + the two cards. Everything else (header, hero, SEO, CTA, footer) is shared.
 */

// Indicative India 2025 sponsored-post rate (₹ per 1,000 followers) for a single
// STATIC post, by niche. Brands pay more where purchase intent + budgets are high
// (finance, beauty, tech). Reels command a premium, stories far less (see mults).
const NICHE_RATE = {
  'Finance / Business': 600,
  'Beauty / Fashion': 650,
  'Tech / Gadgets': 550,
  'Fitness / Health': 450,
  'Travel': 450,
  'Food / Cooking': 400,
  'Education': 400,
  'Lifestyle / Vlog': 350,
  'Comedy / Entertainment': 300,
  'Other': 400,
};
const NICHES = Object.keys(NICHE_RATE);

// Format premium vs a static feed post. Reels are the most in-demand ad unit;
// stories are cheap, disappearing inventory.
const FORMAT_MULT = { 'Reel': 1.6, 'Static post': 1.0, 'Story (x3)': 0.9 };
const FORMATS = Object.keys(FORMAT_MULT);

// Engagement is the single biggest price lever brands negotiate on.
const ENGAGEMENT_MULT = { 'Low (<1%)': 0.7, 'Average (1–3%)': 1.0, 'High (>3%)': 1.5 };
const ENGAGEMENTS = Object.keys(ENGAGEMENT_MULT);

export default function InstagramMoneyToolPage() {
  const [niche, setNiche] = useState(NICHES[0]);
  const [format, setFormat] = useState(FORMATS[0]);
  const [engagement, setEngagement] = useState(ENGAGEMENTS[1]);
  const [followers, setFollowers] = useState('');

  const result = useMemo(() => {
    const f = parseInt(followers, 10) || 0;
    if (!f) return null;
    const rate = NICHE_RATE[niche] || 400;
    const mid = (f / 1000) * rate * FORMAT_MULT[format] * ENGAGEMENT_MULT[engagement];
    if (mid <= 0) return null;
    const step = mid >= 20000 ? 1000 : (mid >= 4000 ? 500 : 100);
    const low = roundTo(mid * 0.75, step);
    const high = roundTo(mid * 1.3, step);
    // Realistic monthly cadence for a creator actively pitching: ~2–4 paid collabs.
    const monthLow = low * 2;
    const monthHigh = high * 4;
    return { low, high, monthLow, monthHigh, yearLow: monthLow * 12, yearHigh: monthHigh * 12 };
  }, [followers, niche, format, engagement]);

  return (
    <ToolShell
      seo={{
        title: 'Instagram Money Calculator India 2025 — Sponsored Rates — CreatorKhata',
        description: 'Free Instagram earnings calculator for Indian creators. Estimate how much to charge brands per sponsored Reel, post or story from your followers, niche and engagement. No signup.',
        path: '/tools/instagram-money-calculator',
      }}
      h1="Instagram Money Calculator (India)"
      sub="How much can you earn on Instagram in India? Enter your followers, niche and engagement to estimate your sponsored Reel / post / story rate — and your monthly brand-deal potential. No signup."
      cta={{
        heading: "You guessed a number. Brands already know theirs.",
        body: 'A calculator gives you a range — landing the deal, pricing it right, sending the invoice and actually getting paid is the real work. CreatorKhata runs your brand-deal pitches, rate card, GST invoices and payment tracking in one place.',
      }}
    >
      {/* Inputs */}
      <div style={card()}>
        <Field label="Niche">
          <select style={input} value={niche} onChange={e => setNiche(e.target.value)}>
            {NICHES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </Field>
        <Field label="Deliverable">
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {FORMATS.map(f => (
              <button key={f} onClick={() => setFormat(f)} style={{
                flex: 1, padding: '0.55rem', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem',
                border: `1.5px solid ${format === f ? ACCENT : '#e0e0e0'}`,
                background: format === f ? ACCENT : 'white', color: format === f ? 'white' : '#666',
              }}>{f}</button>
            ))}
          </div>
        </Field>
        <Field label="Engagement rate">
          <select style={input} value={engagement} onChange={e => setEngagement(e.target.value)}>
            {ENGAGEMENTS.map(en => <option key={en} value={en}>{en}</option>)}
          </select>
        </Field>
        <Field label="Followers">
          <input style={input} type="number" value={followers} onChange={e => setFollowers(e.target.value)} placeholder="e.g. 50000" />
        </Field>
        <p style={{ fontSize: '0.72rem', color: '#aaa', margin: '0.25rem 0 0', lineHeight: 1.5 }}>
          Estimates use 2025 India sponsored-content benchmarks by niche, adjusted for deliverable
          ({format === 'Reel' ? 'Reels command the highest rates' : format === 'Story (x3)' ? 'stories are cheaper, disappearing inventory' : 'static posts are the baseline'}) and engagement. Real rates are negotiated and vary with audience quality, exclusivity & usage rights.
        </p>
      </div>

      {/* Output */}
      <div style={card()}>
        {!result ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: '3rem 1rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📸</div>
            Enter your <b style={{ color: ACCENT }}>followers</b> on the left — your estimated rate appears here <b>instantly</b>. No submit button.
          </div>
        ) : (
          <div>
            <div style={{ borderBottom: `3px solid ${ACCENT}`, paddingBottom: '0.75rem', marginBottom: '0.75rem' }}>
              <div style={{ color: '#888', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Rate per sponsored {format.replace(' (x3)', ' (3-frame)')}</div>
              <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.9rem', color: ACCENT }}>
                {fmt(result.low)} – {fmt(result.high)}
              </div>
            </div>

            <Stat label="Monthly potential (2–4 deals)" value={`${fmt(result.monthLow)} – ${fmt(result.monthHigh)}`} accent />
            <Stat label="Per year" value={`${fmt(result.yearLow)} – ${fmt(result.yearHigh)}`} />

            <div style={{ marginTop: '0.9rem', background: '#f1f8f4', border: `1px solid ${ACCENT}22`, borderRadius: 10, padding: '0.85rem 1rem', fontSize: '0.82rem', color: '#2a5d42' }}>
              💡 On Instagram there's no AdSense — <b>brand deals are the income</b>. Creators who send a proper rate card and invoice get taken seriously (and paid faster) than those who reply "what's your budget?"
            </div>

            <p style={{ fontSize: '0.72rem', color: '#aaa', marginTop: '0.75rem' }}>
              Indicative estimate (2025 India benchmarks). Actual rates depend on audience quality, engagement, niche demand, exclusivity and content usage rights.
            </p>
          </div>
        )}
      </div>
    </ToolShell>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.6rem 0', borderBottom: '1px solid #eee' }}>
      <span style={{ fontSize: '0.85rem', color: '#555', fontWeight: 600 }}>{label}</span>
      <span style={{ fontWeight: 700, fontSize: '0.95rem', color: accent ? ACCENT : INK }}>{value}</span>
    </div>
  );
}
